import { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Search, Stethoscope, Building2, User } from 'lucide-react'
import { getCustomers } from '../services/api'
import { useSort } from '../hooks/useSort'
import SortHeader from '../components/ui/SortHeader'
import Pagination from '../components/ui/Pagination'
import Avatar from '../components/ui/Avatar'
import EmptyState from '../components/ui/EmptyState'
import { useUserMode } from '../contexts/UserModeContext'

const TYPE_CONFIG = {
  DOCTOR:   { label: 'Doctor',   icon: Stethoscope, bg: 'bg-blue-50',    text: 'text-blue-700',    ring: 'ring-blue-200' },
  HOSPITAL: { label: 'Hospital', icon: Building2,    bg: 'bg-purple-50',  text: 'text-purple-700',  ring: 'ring-purple-200' },
  PATIENT:  { label: 'Patient',  icon: User,         bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  OTHER:    { label: 'Other',    icon: null,          bg: 'bg-gray-50',   text: 'text-gray-600',    ring: 'ring-gray-200' },
}

const TYPE_FILTERS = ['ALL', 'DOCTOR', 'HOSPITAL', 'PATIENT', 'OTHER']

function TypeBadge({ type }) {
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.OTHER
  const Icon = cfg.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${cfg.bg} ${cfg.text} ${cfg.ring}`}>
      {Icon && <Icon className="h-3 w-3" />}
      {cfg.label}
    </span>
  )
}

export default function Customers() {
  const [allCustomers, setAllCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('ALL')
  const { isAccountant } = useUserMode()

  useEffect(() => {
    setLoading(true)
    getCustomers({ take: 9999 })
      .then((res) => setAllCustomers(res.data.customers || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  // Client-side search + type filter
  const filtered = useMemo(() => {
    let list = allCustomers
    if (typeFilter !== 'ALL') {
      list = list.filter((c) => c.type === typeFilter)
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      list = list.filter(
        (c) =>
          c.name?.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q) ||
          c.phone?.includes(q) ||
          c.gstin?.toLowerCase().includes(q)
      )
    }
    return list
  }, [allCustomers, search, typeFilter])

  const { paged, page, setPage, totalPages, total, sortKey, sortDir, toggleSort, pageSize } =
    useSort(filtered, { defaultSort: 'name', defaultDir: 'asc', pageSize: 25, storageKey: 'customers' })

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Customers</h1>
        <Link to="/customers/new" className="btn-primary flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> New Customer
        </Link>
      </div>

      {/* Search + Type Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-xs flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search customers..."
            className="input pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-0.5">
          {TYPE_FILTERS.map((t) => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                typeFilter === t
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t === 'ALL' ? 'All' : TYPE_CONFIG[t]?.label || t}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="space-y-3 p-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        ) : paged.length === 0 ? (
          <EmptyState
            type="customers"
            message={
              search || typeFilter !== 'ALL'
                ? 'No customers match your filters.'
                : 'No customers yet. Create your first customer to get started.'
            }
            actionLabel={!search && typeFilter === 'ALL' ? 'Add Customer' : undefined}
            actionTo={!search && typeFilter === 'ALL' ? '/customers/new' : undefined}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 bg-gray-50 border-b">
                    <SortHeader label="Name" sortKey="name" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    <SortHeader label="Type" sortKey="type" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    {isAccountant && (
                      <SortHeader label="GSTIN" sortKey="gstin" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    )}
                    {isAccountant && (
                      <SortHeader label="State" sortKey="billingState" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    )}
                    {isAccountant ? (
                      <>
                        <SortHeader label="Email" sortKey="email" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                        <SortHeader label="Phone" sortKey="phone" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                      </>
                    ) : (
                      <SortHeader label="Contact" sortKey="email" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} />
                    )}
                    <SortHeader label="Invoices" sortKey="_count.invoices" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="right" />
                  </tr>
                </thead>
                <tbody>
                  {paged.map((c) => (
                    <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50 transition-colors">
                      {/* Name with Avatar */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <Avatar name={c.name} />
                          <Link to={`/customers/${c.id}`} className="text-brand-600 hover:underline font-medium">
                            {c.name}
                          </Link>
                        </div>
                      </td>

                      {/* Type badge */}
                      <td className="px-4 py-3">
                        <TypeBadge type={c.type} />
                      </td>

                      {/* GSTIN (accountant only) */}
                      {isAccountant && (
                        <td className="px-4 py-3 text-gray-500 font-mono text-xs">{c.gstin || '\u2014'}</td>
                      )}

                      {/* State (accountant only) */}
                      {isAccountant && (
                        <td className="px-4 py-3 text-gray-500">{c.billingState || '\u2014'}</td>
                      )}

                      {/* Contact columns */}
                      {isAccountant ? (
                        <>
                          <td className="px-4 py-3 text-gray-500">{c.email || '\u2014'}</td>
                          <td className="px-4 py-3 text-gray-500">{c.phone || '\u2014'}</td>
                        </>
                      ) : (
                        <td className="px-4 py-3 text-gray-500">{c.email || c.phone || '\u2014'}</td>
                      )}

                      {/* Invoice count */}
                      <td className="px-4 py-3 text-right text-gray-500">
                        {c._count?.invoices ?? 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} />
          </>
        )}
      </div>
    </div>
  )
}
